/* Turning a kernel DecisionTrace into the shape the trace panel renders.
 *
 * The panel was built against fixture data months before the engine could produce a chain. It is
 * ORBIS's answer to Command PE's "My @#%^@#% weapon won't fire!!!", so it was worth building
 * early — but it has been showing scripted content ever since, and a hero feature that cannot be
 * driven by the engine is a mockup.
 *
 * ── Wording lives here, not in Core ────────────────────────────────────────
 *
 * Core emits stable machine-readable identifiers: `wra_permits`, `close_for_visual_id`. The human
 * label is presentation and belongs in the client, which is why `TraceCheck.check` is documented
 * in Core as "machine-readable — the UI maps it to a human label, so wording can change without
 * breaking consumers". This file is that map.
 *
 * An UNKNOWN check id renders as the raw identifier rather than being dropped. A new link added to
 * the kill chain must show up in the panel immediately, even if ugly — silently hiding a check the
 * engine evaluated would make the panel lie about what was tested, which is the one thing it must
 * never do.
 */

import type { DecisionTrace, TraceCheck } from '../data/types'
import { authToken, baseUrl } from './gateway'

/** A trace as it arrives from the Gateway (the serialised `orbis_core.trace.DecisionTrace`). */
export interface RawTrace {
  decision: string
  subject?: string
  target?: string
  outcome: 'permitted' | 'declined'
  reason: string
  chain: { check: string; result: string; values?: Record<string, unknown> }[]
  model: { stage: string; implementation: string; plugin_id?: string; plugin_version?: string }
  remedies?: string[]
  tick: number
  sim_time_ns: number
}

/** Human labels for the kill chain and the sense chain. */
const CHECK_LABELS: Record<string, string> = {
  // Kill chain (SPEC-01 §8.1)
  contact_held: 'Contact held in side picture',
  classification: 'Classification sufficient',
  roe_permits: 'ROE permits engagement',
  wra_permits: 'WRA permits weapon release',
  director_available: 'Director can track',
  illuminator_available: 'Illuminator available',
  mount_available: 'Mount operational and in arc',
  magazine_has_rounds: 'Magazine holds rounds',
  within_dlz: 'Target inside launch zone',
  engagement_slots: 'Engagement slots free',
  // Endgame
  target_present: 'Target still present',
  guidance_link: 'Guidance link held',
  seeker_lock: 'Seeker acquired',
  soft_kill: 'Survived countermeasures',
  point_defence: 'Survived point defence',
  warhead_effect: 'Warhead effective',
  damage_applied: 'Damage applied',
  // Sense
  sensor_available: 'Sensor operational',
  emitting: 'Sensor radiating',
  target_alive: 'Target alive',
  not_own_side: 'Target is not own side',
  outside_min_range: 'Outside minimum range',
  within_arc: 'Within sensor arc',
  line_of_sight: 'Line of sight',
  within_detection_range: 'Within detection range',
  probability_roll: 'Detection roll',
  target_emitting: 'Target is emitting',
  // Motion
  entity_alive: 'Unit alive',
  propulsion_available: 'Propulsion operational',
  fuel_remaining: 'Fuel remaining',
  has_speed: 'Ordered speed set',
  following_course: 'Following plotted course',
  holding_heading: 'Holding heading',
  fuel_burn: 'Fuel burn',
  bingo_crossed: 'Bingo fuel crossed',
  waypoints_reached: 'Waypoints reached',
}

/** Remedies, as an action label plus the control an operator would use. */
const REMEDIES: Record<string, { label: string; target: string }> = {
  request_posture_change: { label: 'Set side posture to WEAPONS FREE', target: 'posture' },
  close_for_visual_id: { label: 'Close for visual identification', target: 'course' },
  task_esm_intercept: { label: 'Task ESM for an intercept', target: 'sensors' },
  rearm_with_compatible_weapon: { label: 'Rearm with a compatible weapon', target: 'rearm' },
  use_fire_and_forget_weapon: { label: 'Switch to a fire-and-forget weapon', target: 'weapons' },
  use_active_weapon: { label: 'Switch to an active-homing weapon', target: 'weapons' },
  repair_director: { label: 'Repair the fire-control director', target: 'damage' },
  repair_illuminator: { label: 'Repair the illuminator', target: 'damage' },
  repair_mount: { label: 'Repair the mount', target: 'damage' },
  turn_to_unmask: { label: 'Turn to unmask the mount', target: 'course' },
  rearm: { label: 'Rearm', target: 'rearm' },
  reduce_shot_doctrine: { label: 'Reduce shot doctrine to one round', target: 'doctrine' },
  close_range: { label: 'Close the range', target: 'course' },
  open_range: { label: 'Open the range', target: 'course' },
  wait_for_impact: { label: 'Wait for the current salvo to resolve', target: 'time' },
  set_emcon_active: { label: 'Set EMCON to radiating', target: 'emcon' },
  use_active_sensor: { label: 'Use an active sensor', target: 'emcon' },
  climb_for_los: { label: 'Climb for line of sight', target: 'course' },
  search_with_active_sensor: { label: 'Search with an active sensor', target: 'emcon' },
  wait_for_track_update: { label: 'Wait for a track update', target: 'time' },
}

const DECISION_HEADLINES: Record<string, (t: RawTrace) => string> = {
  weapon_release: (t) =>
    t.outcome === 'declined'
      ? `Cannot engage ${short(t.target)}`
      : `Engaging ${short(t.target)}`,
  damage_assessment: (t) => `Weapon effect on ${short(t.target)}`,
  detection: (t) =>
    t.outcome === 'declined' ? `Cannot detect ${short(t.target)}` : `Detected ${short(t.target)}`,
  kinematics: () => 'Motion',
}

function short(id?: string): string {
  if (!id) return 'target'
  // Entity ids are long; the last group is enough to distinguish them on a single line.
  const parts = id.split('-')
  return parts[parts.length - 1] || id
}

/** Format a check's values for display, keeping the units the engine used. */
function formatValues(values?: Record<string, unknown>): Record<string, string> | undefined {
  if (!values) return undefined
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(values)) {
    if (v === null || v === undefined) continue
    // Snake case is how the engine names things and how a bug report should quote them, so the
    // key is only prettified, never renamed.
    const key = k.replace(/_/g, ' ')
    out[key] = typeof v === 'number' ? String(Math.round(v * 1000) / 1000) : String(v)
  }
  return Object.keys(out).length ? out : undefined
}

export function toDecisionTrace(raw: RawTrace, epochIso: string, index: number): DecisionTrace {
  const chain: TraceCheck[] = raw.chain.map((c) => ({
    check: c.check,
    // Unknown ids render raw rather than being dropped: a new kill-chain link must appear in the
    // panel immediately, even if it appears ugly.
    label: CHECK_LABELS[c.check] ?? c.check,
    result: (c.result as TraceCheck['result']) ?? 'not_evaluated',
    values: formatValues(c.values),
  }))

  const headline = (DECISION_HEADLINES[raw.decision] ?? (() => raw.decision))(raw)

  return {
    id: `trace-${raw.tick}-${index}`,
    simTime: simClock(epochIso, raw.sim_time_ns),
    decision: raw.decision.replace(/_/g, ' ').toUpperCase(),
    headline,
    subject: raw.subject ?? '',
    outcome: raw.outcome,
    reason: raw.reason,
    chain,
    model: {
      stage: raw.model.stage,
      implementation: raw.model.implementation,
      pluginId: raw.model.plugin_id,
      pluginVersion: raw.model.plugin_version,
    },
    remedies: (raw.remedies ?? [])
      .map((r) => REMEDIES[r] ?? { label: r.replace(/_/g, ' '), target: r })
      .filter(Boolean),
  }
}

function simClock(epochIso: string, simTimeNs: number): string {
  const ms = Date.parse(epochIso) + (simTimeNs ?? 0) / 1e6
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(11, 19) : '--:--:--'
}

/** Fetch traces for one unit.
 *
 * Through `baseUrl()` like every other call, NOT a bare same-origin path. A hardcoded `/v1/…`
 * is right in the deployment and silently wrong under `vite dev`, where it hits the dev server
 * and 404s — and because the failure is swallowed below, the panel then shows fixture traces
 * while the status bar says LIVE. A wrong picture that announces itself as the real one is the
 * exact failure this project keeps finding. */
export async function fetchTraces(entityId: string): Promise<RawTrace[]> {
  try {
    const res = await fetch(`${baseUrl()}/v1/trace/${encodeURIComponent(entityId)}?limit=4`, {
      headers: { Authorization: `Bearer ${authToken()}` },
    })
    if (!res.ok) return []
    const body = (await res.json()) as { traces?: RawTrace[] }
    return body.traces ?? []
  } catch {
    // Offline. The panel falls back to fixture traces and the status bar already says FIXTURE, so
    // there is nothing useful to report here.
    return []
  }
}


const POSTURE_LABELS: Record<string, string> = {
  weapons_free: 'WEAPONS FREE',
  weapons_tight: 'WEAPONS TIGHT',
  weapons_hold: 'WEAPONS HOLD',
  inherited: 'inherited',
}

/** The resolved doctrine chain for one unit, from the engine.
 *
 * All four levels come back, including the ones that set nothing — "not set here" is a fact about
 * where to go next, and a list showing only the winning level would hide it.
 */
export async function fetchDoctrine(entityId: string): Promise<
  { level: 'Side' | 'Mission' | 'Group' | 'Unit'; scope: string; value: string; isSource: boolean }[]
> {
  try {
    const res = await fetch(`${baseUrl()}/v1/doctrine/${encodeURIComponent(entityId)}`, {
      headers: { Authorization: `Bearer ${authToken()}` },
    })
    if (!res.ok) return []
    const body = (await res.json()) as {
      levels?: { level: string; scope: string; value: string; isSource: boolean }[]
    }
    // Wording is the client's, as everywhere else: the engine emits stable identifiers
    // (`weapons_tight`) and the UI decides how to say them.
    return (body.levels ?? []).map((l) => ({
      ...l,
      level: l.level as 'Side' | 'Mission' | 'Group' | 'Unit',
      value: POSTURE_LABELS[l.value] ?? l.value,
    }))
  } catch {
    return []
  }
}
